import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { NotificationsService } from '../services/NotificationsService';
import { AppNotification, AppNotificationModel, NOTIFICATION_TYPE_INFO } from '../models/AppNotification';

// Mirrors iOS UserNotificationsView
const NotificationsView: React.FC = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    const unsub = NotificationsService.listen(
      user.id,
      (items) => { setNotifications(items); setIsLoading(false); },
      (err) => { setError(err.message); setIsLoading(false); }
    );
    return unsub;
  }, [user]);

  const handleOpen = async (n: AppNotification) => {
    if (!user || AppNotificationModel.hasUserRead(n, user.id)) return;
    try {
      await NotificationsService.markAsRead(n.id, user.id);
    } catch (e) {
      console.error('Failed to mark notification read:', e);
    }
  };

  const relative = (d: Date) => {
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  if (isLoading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Loading notifications…</div>;
  }

  return (
    <div style={{ padding: '16px', maxWidth: '640px', margin: '0 auto', overflowY: 'auto', height: '100%' }}>
      <h2 style={{ margin: '0 0 16px', fontSize: '22px' }}>🔔 Notifications</h2>
      {error && <p style={{ color: '#ff3b30' }}>{error}</p>}

      {notifications.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
          <p style={{ fontSize: '16px', fontWeight: 600, color: '#666' }}>You're all caught up!</p>
        </div>
      ) : (
        notifications.map(n => {
          const typeInfo = NOTIFICATION_TYPE_INFO[n.type as keyof typeof NOTIFICATION_TYPE_INFO] || { displayName: n.type, emoji: '🔔', color: '#007aff' };
          const read = user ? AppNotificationModel.hasUserRead(n, user.id) : true;
          return (
            <div
              key={n.id}
              onClick={() => handleOpen(n)}
              style={{
                display: 'flex', gap: '12px', padding: '14px 16px', marginBottom: '10px',
                backgroundColor: read ? 'white' : '#f0f6ff',
                borderRadius: '12px', border: `1px solid ${read ? '#e8e8e8' : '#007aff33'}`,
                cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}
            >
              <div style={{ fontSize: '24px' }}>{typeInfo.emoji}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <strong style={{ fontSize: '15px', color: '#222' }}>{n.title}</strong>
                  <span style={{ fontSize: '12px', color: '#999', whiteSpace: 'nowrap', marginLeft: '8px' }}>
                    {relative(n.timestamp)}
                  </span>
                </div>
                <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#555' }}>{n.message}</p>
                {n.imageURL && (
                  <img src={n.imageURL} alt={n.imageCaption ?? ''} style={{ marginTop: '8px', maxWidth: '100%', borderRadius: '8px' }} />
                )}
                <span style={{
                  display: 'inline-block', marginTop: '6px', fontSize: '11px', fontWeight: 600,
                  color: typeInfo.color, textTransform: 'uppercase', letterSpacing: '0.4px'
                }}>
                  {typeInfo.displayName}
                </span>
              </div>
              {!read && (
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#007aff', alignSelf: 'center' }} />
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default NotificationsView;
